import { ISmsAutoRechargeSettings, SMS_AUTO_RECHARGE_FAILURE_CODES } from '@interfaces/sms-credits';
import { convertPrice } from '@utils/resource';
import { Alert } from 'antd';
import { useTranslation } from 'react-i18next';

type Props = {
    settings?: ISmsAutoRechargeSettings;
    locked: boolean;
    enabled: boolean;
    adminMax: number | null;
    packageUnavailable: boolean;
    hasDefaultCard: boolean;
};

/** Why auto-recharge is limited, off or failing: shown above the form, most blocking first. */
export const SmsAutoRechargeStatusAlerts: React.FC<Props> = ({
    settings,
    locked,
    enabled,
    adminMax,
    packageUnavailable,
    hasDefaultCard,
}) => {
    const { t } = useTranslation(['marketplace']);
    const failureCode =
        settings?.lastFailureCode &&
        SMS_AUTO_RECHARGE_FAILURE_CODES.includes(settings.lastFailureCode)
            ? settings.lastFailureCode
            : 'payment_error';

    const alerts: { key: string; type: 'warning' | 'info' | 'error'; message: string }[] = [];
    if (locked)
        alerts.push({
            key: 'locked',
            type: 'warning',
            message: t('smsCredits.autoRecharge.adminDisabled'),
        });
    if (settings?.lastFailureCode || settings?.lastFailure)
        alerts.push({
            key: 'failure',
            type: 'error',
            message: t('smsCredits.autoRecharge.lastFailure', {
                reason: t(`smsCredits.autoRecharge.failureCodes.${failureCode}`),
            }),
        });
    if (!locked && enabled && packageUnavailable)
        alerts.push({
            key: 'package',
            type: 'warning',
            message: t('smsCredits.autoRecharge.packageUnavailable'),
        });
    if (!hasDefaultCard && !enabled)
        alerts.push({ key: 'card', type: 'warning', message: t('smsCredits.purchase.noCard') });
    if (!locked && adminMax != null)
        alerts.push({
            key: 'max',
            type: 'info',
            message: t('smsCredits.autoRecharge.adminMax', { max: convertPrice(adminMax) }),
        });

    if (!alerts.length) return null;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
            {alerts.map((a) => (
                <Alert key={a.key} type={a.type} showIcon message={a.message} />
            ))}
        </div>
    );
};
